import { Component, OnInit, ViewChild } from '@angular/core';
import { Common } from 'src/app/common';
import * as _ from 'lodash';
import { PaymentService } from 'src/app/services/payment.service';
import { PaymentMethod } from 'src/app/models/payment/payment-method.model';
import { SessionService } from 'src/app/services/session.service';
import { InitialisePaymentResult } from 'src/app/models/payment/initialise-payment-result.model';
import { PaymentDetails } from 'src/app/models/payment/payment-details.model';
import { PaymentAccount } from 'src/app/models/payment/payment-account.model';
import { CurrencyExchangeService } from 'src/app/services/currency-exchange.service';
import { map, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { AppConfigService } from 'src/app/services/app-config.service';
import { DialogResult, DialogService } from '@progress/kendo-angular-dialog';
import { NabCardPaymentDialogComponent } from '../../dialog/nab-card-payment-dialog/nab-card-payment-dialog.component';
import { CreditCardDetails } from 'src/app/models/payment/credit-card-details.model';

@Component({
  selector: 'app-pay-in-full',
  templateUrl: './pay-in-full.component.html',
  styleUrls: ['./pay-in-full.component.scss']
})
export class PayInFullComponent implements OnInit {
  @ViewChild('paymentForm') public paymentForm: NgForm | undefined;

  private paymentDetails = new PaymentDetails();
  private selectedDebtAccountIds: Array<string> = [];
  private selectedPaymentCountry = '';
  public selectedPaymentMethod = '';

  public alternativeAmount: any = null;
  public initialised = false;
  public showBaseScreen = true;
  public showSummaryScreen = false;
  public processing = false;
  public paymentSummary = new InitialisePaymentResult();

  constructor(
    private paymentService: PaymentService,
    private currencyExchangeService: CurrencyExchangeService,
    private sessionService: SessionService,
    private appConfigService: AppConfigService,
    private dialogService: DialogService,
    private router: Router) {
  }

  ngOnInit(): void {
    setTimeout(() => this.loadPaymentDetails(), 0);
  }

  private loadPaymentDetails(): void {
    this.sessionService.showLoading();

    this.paymentService.getPaymentDetails().subscribe({
      next: (result) => this.loadPaymentDetailsOk(result),
      error: (error) => this.loadPaymentDetailsError(error.error)
    });
  }

  private loadPaymentDetailsOk(result: PaymentDetails): void {
    this.paymentDetails = result;

    this.setDefaultCountry();
    this.setDefaultPaymentMethod();

    this.paymentDetails.accounts.forEach((account) => {
      this.selectedDebtAccountIds.push(account.accountId);
    });

    this.setAlternativeAmountDefault();

    this.initialised = true;
    this.sessionService.hideLoading();
  }

  private loadPaymentDetailsError(error: any): void {
    this.sessionService.hideLoading();
    this.sessionService.showErrorDialog(error.message);

    this.router.navigate(['/']);
  }

  public isDebtAccountSelected(accountId: string): boolean {
    return _.some(this.selectedDebtAccountIds, (id) => id === accountId);
  }

  public toggleDebtAccount(accountId: string): void {
    if (this.isDebtAccountSelected(accountId)) {
      _.remove(this.selectedDebtAccountIds, (id) => id === accountId);
    } else {
      this.selectedDebtAccountIds.push(accountId);
    }

    this.setAlternativeAmountDefault();
  }

  private setAlternativeAmountDefault(): void {
    this.alternativeAmount = _.round(this.getSelectedTotalAmount(), 2);
  }

  public getAlternativeAmountMaximum(): number {
    return _.round(this.getSelectedTotalAmount(), 2);
  }

  public getDebtAccounts(): Array<PaymentAccount> {
    return this.paymentDetails.accounts;
  }

  public getPaymentCountries(): Array<string> {
    return _.map(this.paymentDetails.countries, (country) => country.countryName);
  }

  public getPaymentMethods(): Array<PaymentMethod> {
    return _.find(this.paymentDetails.countries, (country) => country.countryName === this.selectedPaymentCountry)?.payMethods ?? [];
  }

  public getDebtAccountDescription(account: PaymentAccount): string {
    return `${account.portfolioDesc} ${this.formatCurrency(account.totalOutstanding)} ${this.getPaymentCurrency()}`;
  }

  private getSelectedCountryId(): string {
    const selectedCountry = _.find(this.paymentDetails.countries, (country) => country.countryName === this.selectedPaymentCountry);
    
    return selectedCountry?.countryId ?? '';
  }

  public getSelectedTotalAmount(): number {
    let total = 0;

    this.getSelectedDebtAccounts().forEach((debtAccount) => {
      total += debtAccount.totalOutstanding;
    });

    return total;
  }

  private getSelectedDebtAccounts(): Array<PaymentAccount> {
    const result: Array<PaymentAccount> = [];

    this.selectedDebtAccountIds.forEach((debtAccountId) => {
      const selectedDebtAccount = _.find(this.getDebtAccounts(), (debtAccount) => debtAccount.accountId === debtAccountId);

      if (!_.isNil(selectedDebtAccount)) {
        result.push(selectedDebtAccount);
      }
    });

    return result;
  }

  public getSelectedTotal(): string {
    return this.formatCurrency(this.getSelectedTotalAmount());
  }

  public setPaymentCountry(paymentCountry: string): void {
    this.processing = true;
    this.selectedPaymentCountry = paymentCountry;

    this.setDefaultPaymentMethod();
  }

  public setDefaultCountry(): void {
    if (this.paymentDetails.countries.length > 0) {
      this.selectedPaymentCountry = this.paymentDetails.countries[0].countryName;
    } else {
      this.selectedPaymentCountry = '';
    }
  }

  public setDefaultPaymentMethod(): void {
    this.processing = true;
    const paymentMethods = this.getPaymentMethods();
    let defaultPaymentMethod = new PaymentMethod();

    if (paymentMethods.length > 0) {
      defaultPaymentMethod = paymentMethods[0]
    }

    this.setPaymentMethod(defaultPaymentMethod);
  }

  public setPaymentMethod(paymentMethod: PaymentMethod): void {
    this.processing = true;
    const paymentCurrency = this.getPaymentCurrency();

    // When setting the selected payment method, we want to pre cache the exchange rate if applicable, otherwise the view could cause more HTTP calls then required
    if (paymentCurrency !== '' && paymentMethod.currency !== '' && paymentCurrency !== paymentMethod.currency) {
      this.currencyExchangeService.convert(paymentCurrency, paymentMethod.currency, 1).subscribe({
        next: () => {
          this.selectedPaymentMethod = paymentMethod.payMethod;
          this.processing = false;
        },
        error: () => {
          this.sessionService.showErrorDialog('Error retrieving currency exchange information. Please try selecting the payment method again or alternatively try another payment method.');
          this.setDefaultCountry();
          this.setDefaultPaymentMethod();
        }
      })
    } else {
      this.selectedPaymentMethod = paymentMethod.payMethod;
      this.processing = false;
    }
  }

  public isCountrySelectVisible(): boolean {
    return this.getPaymentCountries().length > 1;
  }

  public isCountrySelected(country: string): boolean {
    return this.selectedPaymentCountry === country;
  }

  public isPaymentMethodSelected(paymentMethod: PaymentMethod): boolean {
    return this.selectedPaymentMethod === paymentMethod.payMethod;
  }

  public isPaymentAmountInMethodCurrencyVisible(): boolean {
    const selectedPaymentMethod = this.getSelectedPaymentMethodObject();
    const paymentCurrency = this.getPaymentCurrency();

    return !_.isNil(selectedPaymentMethod) && selectedPaymentMethod.currency != '' && paymentCurrency !== '' && selectedPaymentMethod.currency !== paymentCurrency;
  }

  public getTotalAmountInMethodCurrency(): Observable<string> {
    return this.getAmountInMethodCurrency(this.getSelectedTotalAmount());
  }

  public getPaymentAmountInMethodCurrency(): Observable<string> {
    return this.getAmountInMethodCurrency(this.alternativeAmount);
  }

  private getAmountInMethodCurrency(amount: number): Observable<string> {
    const paymentMethodCurrency = this.getSelectedPaymentMethodObject()?.currency ?? '';

    return this.currencyExchangeService.convert(this.getPaymentCurrency(), paymentMethodCurrency, amount).pipe(map((
      result: number) => `(${this.formatCurrency(result)} ${paymentMethodCurrency})`));
  }

  public getSelectedPaymentMethodObject(): PaymentMethod | undefined {
    return _.find(this.getPaymentMethods(), (method) => method.payMethod === this.selectedPaymentMethod);
  }

  public getSummaryHeading(): string {
    let result = '';

    switch (this.selectedPaymentMethod) {
      case 'BPAY':
        result = 'Instructions for BPAY payment.';
        break;
      case 'MANUAL':
        result = 'Instructions for payment by Bank Deposit.';
        break;
    }

    return result;
  }

  public getSummaryDebts(): string {
    let result = '';

    this.getSelectedDebtAccounts().forEach((debtAccount) => {
      if (result !== '') {
        result += ', ';
      }

      result += this.getDebtAccountDescription(debtAccount);
    });

    return result;
  }

  public getPaymentCurrency(): string {
    return this.paymentDetails.accounts.length > 0 ? this.paymentDetails.accounts[0].currency : '';
  }

  public getSummaryTotal(): string {
    return `${this.getSelectedTotal()} ${this.getPaymentCurrency()}`;
  }

  public isNextDisabled(): boolean {
    return this.processing;
  }

  private isInvalid(): boolean {
    return _.isNil(this.paymentForm) || this.paymentForm.invalid || this.selectedDebtAccountIds.length === 0 || this.selectedPaymentMethod === '' ||
      this.sessionService.isProcessing() || this.alternativeAmount < 1;
  }

  public onNextClick(): void {
    if (!this.isInvalid()) {
      const selectedPaymentMethod = this.getSelectedPaymentMethodObject();

      if (!_.isNil(selectedPaymentMethod)) {
        this.handleInitialisePaymentCLick(selectedPaymentMethod);
      } else {
        // Shouldn't reach here, but something has gone wrong if we do?
      }
    }
  }

  public isCollexusVersion(): boolean {
    return this.appConfigService.getConfig().htmlVersion !== 'version1';
  }

  private handleInitialisePaymentCLick(paymentMethod: PaymentMethod): void {
    this.sessionService.showProcessing();

    this.paymentService.initialisePayment(this.calculatePaymentAmount(), this.selectedDebtAccountIds, this.getSelectedCountryId(), paymentMethod.payMethod,
      this.getPaymentCurrency(), paymentMethod.currency).subscribe({
      next: (result) => {
        switch (paymentMethod.payMethod) {
          case 'OLEFT':
          case 'CARD':
            this.handleInitialiseCardPayment(result);
            break;
          case 'BPAY':
          case 'MANUAL':
            this.paymentSummary = result;
            this.showBaseScreen = false;
            this.showSummaryScreen = true;
            this.sessionService.hideProcessing();
            break;
        }   
      },
      error: (error) => {
        this.sessionService.hideProcessing();
        this.sessionService.showErrorDialog(error.error.message);
      }
    });
  }

  private handleInitialiseCardPayment(result: InitialisePaymentResult): void {
    switch (result.supplierId) {
      case 'NAB':
        this.handleInitialiseNabCardPayment(result);
        break;
      case 'MW':
        this.handleInitialiseMwCardPayment(result);
        break;
      case 'PAYMARK':
        this.sessionService.hideProcessing();
        document.location.href = result.url ?? '';
        break;
    }
  }

  private handleInitialiseNabCardPayment(result: InitialisePaymentResult): void {
    const paymentDialog = this.dialogService.open({
      content: NabCardPaymentDialogComponent
    });

    paymentDialog.result.subscribe((dialogResult: any) => {
      if (!_.isNil(dialogResult?.details)) {
        const cardDetails = dialogResult.details as CreditCardDetails;

        Common.createInitialisePaymentNABDirectPostForm(result, cardDetails).then((form) => {
          document.body.appendChild(form);
          this.sessionService.hideProcessing();
          form.submit();
        });
      } else {
        this.sessionService.hideProcessing();
      }
    });
  }

  private handleInitialiseMwCardPayment(result: InitialisePaymentResult): void {
    Common.createInitialisePaymentMWForm(result).then((form) => {
      document.body.appendChild(form);
      this.sessionService.hideProcessing();
      form.submit();
    });
  }

  public onSummaryPreviousButtonClick(): void {
    this.showBaseScreen = true;
    this.showSummaryScreen = false;
  }

  public onSummaryNextButtonClick(): void {
    this.router.navigate(['/']);
  }

  public formatCurrency(amount: number): string {
    return Common.getFormattedCurrencyValue(amount, false, false);
  }

  private calculatePaymentAmount(): number {
    let amount = 0;

    if (_.isNil(this.alternativeAmount)) {
      this.selectedDebtAccountIds.forEach((id) => {
        const selectedDebtAccount = _.find(this.paymentDetails.accounts, (debtAccount) => debtAccount.accountId === id);
  
        if (!_.isNil(selectedDebtAccount)) {
          amount += selectedDebtAccount.totalOutstanding;
        }
      });
    } else {
      amount = this.alternativeAmount;
    }

    return amount;
  }
}
